import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Dictate a note instead of typing it.

   A rep standing outside a building has one hand, a phone and thirty seconds. Typing
   a visit note there is the step that does not happen, so the note is missing and the
   CRM is poorer for it. This is the browser's own speech recognition — free, on the
   device, no audio ever uploaded and none stored.

   ★ The transcript is NOT the record. It lands in a textarea the rep can correct
   before anything is saved: recognition mangles company names and it must never be
   the case that speaking produced a note nobody could review.

   ★ THE MICROPHONE CLOSES BY ITSELF. Both engines end the session after a few
   seconds of silence — which is precisely what a person does between two sentences —
   and `continuous` does not prevent it, least of all on iOS. Dictation that is not
   reopened therefore stops after the first clause and looks broken. `wantRef` holds
   the rep's intent (they pressed the button, they have not pressed stop) and onend
   reopens against it; `baseRef` keeps what was already finalised, because each new
   session starts its results from zero and would otherwise overwrite the sentence
   before it.

   ★ EVERY FAILURE HAS TO BE SAYABLE. A denied microphone permission raises one
   event and nothing else happens — no audio, no error, no clue. `error` carries the
   engine's own code out to the caller so the screen can say which of the four real
   causes it was instead of leaving somebody tapping a dead button.

   Support is genuinely partial — Chrome and Safari have it behind the webkit prefix,
   Firefox does not have it at all, and neither does any browser on iOS that is not
   Safari itself — so `supported` is part of the contract and every caller must still
   render the plain textarea. There is no polyfill worth adding: the alternative is
   uploading audio to a paid transcription service. */
const getRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

// Region-tagged deliberately. A bare 'nl' is accepted and then recognised against
// Netherlands Dutch, which mishears a Flemish rep often enough to matter; 'nl-BE'
// is a different acoustic model, not a formality.
const LANGS = { nl: 'nl-BE', fr: 'fr-BE', en: 'en-GB' };
const speechLang = (language) => {
  const code = (language || 'nl').toLowerCase();
  return code.includes('-') ? code : (LANGS[code] || code);
};

// How many times the engine may close and be reopened without a single word coming
// back before we accept that nothing is being heard. Each silent session runs for a
// handful of seconds, so this is roughly a minute of quiet — long enough for someone
// to think, short enough that a dead microphone does not hold the light on forever.
const MAX_SILENT_RESTARTS = 8;

const useSpeechToText = () => {
  const { i18n } = useTranslation();
  const Recognition = getRecognition();
  const supported = !!Recognition;

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  // Everything finalised so far, including whatever was already in the box when
  // dictation started — so a second burst appends to the rep's edits.
  const baseRef = useRef('');
  // The rep's intent, not the engine's state. Only `stop()` and a real failure
  // clear it; a session ending on silence does not.
  const wantRef = useRef(false);
  const silentRestartsRef = useRef(0);

  const stop = useCallback(() => {
    wantRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    setListening(false);
  }, []);

  const start = useCallback((existing = '') => {
    if (!supported || wantRef.current) return;
    setError(null);
    baseRef.current = existing || '';
    silentRestartsRef.current = 0;
    wantRef.current = true;

    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = speechLang(i18n.language);

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event) => {
      // From resultIndex, not from zero: with continuous on, earlier results are
      // already folded into baseRef and re-reading them doubles the sentence.
      let settled = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) settled += result[0].transcript;
        else interim += result[0].transcript;
      }
      if (settled.trim()) {
        const sep = baseRef.current && !/\s$/.test(baseRef.current) ? ' ' : '';
        baseRef.current = baseRef.current + sep + settled.trim();
        silentRestartsRef.current = 0;
      }
      const gap = baseRef.current && interim.trim() && !/\s$/.test(baseRef.current) ? ' ' : '';
      setTranscript(baseRef.current + gap + interim.trim());
    };

    recognition.onerror = (event) => {
      const code = event.error;
      // A pause between sentences, or our own stop() — onend handles both.
      if (code === 'no-speech' || code === 'aborted') return;
      // Anything else is a real cause the rep can act on (permission, no
      // microphone, no connection). Stop wanting it, or onend would loop on it.
      wantRef.current = false;
      setError(code || 'unknown');
      setListening(false);
    };

    recognition.onend = () => {
      if (wantRef.current && silentRestartsRef.current < MAX_SILENT_RESTARTS) {
        silentRestartsRef.current += 1;
        try {
          recognition.start();
          return;
        } catch { /* the engine refused to reopen — fall through and stop */ }
      }
      wantRef.current = false;
      setListening(false);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      // start() throws when the microphone is already in use — another tab, or a
      // session this one did not clean up.
      wantRef.current = false;
      setError('busy');
      setListening(false);
    }
  }, [Recognition, supported, i18n.language]);

  // A rep who navigates away mid-sentence must not leave the microphone open —
  // and must not have onend reopen it behind a screen that is gone.
  useEffect(() => () => {
    wantRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* nothing to stop */ }
  }, []);

  return { supported, listening, transcript, error, start, stop, setTranscript };
};

export default useSpeechToText;
